import { useCallback, useContext, useRef, useState } from 'react';
import {
  PromptContext,
  SnackbarContext,
  SnackbarVariant,
} from '@eas/common-web';
import { validateWorkOrder } from '@modules/work-order/work-order-api';
import { useWorkOrderExceptions } from '@composite/planner/work-order-exceptions';
import { mobileUnitHardExceptions } from '@enums';
import { WorkOrderDto } from '../utils/prepare-dto';

export function useValidateWorkOrder() {
  const { testPrompt } = useContext(PromptContext);
  const [result, setResult] = useState<string[]>([]);
  const { showSnackbar } = useContext(SnackbarContext);
  const skipNextValidationsRef = useRef(false);

  const [isConfirmAllAllowed, setIsConfirmAllAllowed] = useState(false);
  const resolveRef = useRef<((value: { ok: boolean }) => void) | null>(null);

  const handleConfirmAll = () => {
    skipNextValidationsRef.current = true;
    setResult([]);
    if (resolveRef.current) {
      resolveRef.current({ ok: true });
      resolveRef.current = null;
    }
  };

  useWorkOrderExceptions({
    exceptions: result,
    onConfirmAll: isConfirmAllAllowed ? handleConfirmAll : undefined,
  });

  const validate = async (
    workOrder: WorkOrderDto,
    isMultiDay = false,
    allowConfirmAll = false
  ) => {
    let errors: string[] = [];
    if (skipNextValidationsRef.current) {
      console.log('returning from validate lebo all ok');
      return { allOk: true };
    }
    try {
      errors = await validateWorkOrder(
        workOrder.id,
        {
          ownerType: workOrder.unitType,
          ownerId: workOrder.unitId,
          plannedFrom: workOrder.plannedFrom,
          plannedTo: workOrder.plannedTo,
        },
        isMultiDay
      ).json();
    } catch (error) {
      console.log(error);
      const errorCode = (error as { code: string })?.code ?? 'DEFAULT';
      const msg =
        mobileUnitHardExceptions?.[errorCode] ??
        mobileUnitHardExceptions.DEFAULT;
      showSnackbar(msg, SnackbarVariant.ERROR);
      return { ok: false };
    }

    setResult(errors);
    setIsConfirmAllAllowed(allowConfirmAll);

    const promise = new Promise<{ ok: boolean }>((resolve) => {
      resolveRef.current = resolve;
      setTimeout(() => {
        if (errors.length === 0) {
          resolve({
            ok: true,
          });
          return;
        }

        testPrompt({
          key: 'VALIDATE_WORK_DIALOG',
          submitCallback: () => {
            resolve({
              ok: true,
            });
          },
          closeCallback: () => {
            resolve({
              ok: false,
            });
          },
        });
      }, 0);
    });

    return promise;
  };

  const reset = useCallback(() => {
    skipNextValidationsRef.current = false;
    setResult([]);
    setIsConfirmAllAllowed(false);
  }, []);

  return { validate, reset };
}
